/** Durable Object storage-key SSOT for the User and ChatRoom DO surfaces, plus the DO message-batch size and legacy
 *  Creator image-key prefix. Enforcement adjudication: NO bare-key forbid-rule -- these short property-shaped tokens collide with table and JS property names, so a ban would be an FP factory. */

import { MAX_PAGE_SIZE } from '@federa/const/config-pagination';
import { TIME_UNITS } from '@federa/const/time-units';

export const DO_STORAGE_KEYS = {
	USER_DATA: 'userData',
	ROLE: 'role',
	CURRENT_BALANCE: 'current_balance',
	FAVORITE_PROPERTIES: 'favoriteProperties',
	WISHLISTS: 'wishlists',
	PROPERTY_WISHLIST_MAP: 'propertyWishlistMap',
	ACTIVITIES: 'activities',
	BIDS: 'bids',
	CURRENT_BATCH_INDEX: 'currentBatchIndex',
	ACTIVITIES_BATCH_PREFIX: 'activities_batch_',
	ACTIVITY_DEDUP_PREFIX: 'activity_dedup_',
	SCHEMA_SYNCED: 'schema_synced',
} as const;

/** Number of messages per batch in DO storage */
export const MESSAGE_BATCH_SIZE = 50;

/**
 * How many message batches ONE conversation keeps in DO storage before the oldest are evicted.
 *
 * DERIVED, not chosen: `QUERY_MESSAGES_RECENT` (const/sql-conversations-messages-recent.ts) caps its read at
 * `MAX_PAGE_SIZE`, so this is exactly the window the D1 read the cache MIRRORS can itself serve. Spelling a larger
 * number would cache history no reader can reach; a smaller one would make the cache answer short of D1 for a
 * conversation the D1 path would have answered in full. Older-than-this falls back to D1 by design -- an arm no surface
 * reaches today, because none reads deeper than `MAX_PAGE_SIZE`.
 */
export const MESSAGE_BATCH_CACHE_LIMIT = MAX_PAGE_SIZE / MESSAGE_BATCH_SIZE;

/**
 * How long a ChatRoom message cache stays trusted without re-reading D1 -- the LEASE, refreshed by every append.
 *
 * IT IS A LEASE AND NOT A ONE-SHOT MARKER for a reason a boolean cannot express. Every append is AWAITED by the flow
 * that just inserted the row, so a failed append is known to its producer and clears the lease. Two holes stay open to
 * that: the producer dying between the D1 insert and the append, and the DO being unreachable for it. Under a boolean
 * marker either one leaves the cache permanently and silently short a message, in a chat -- strictly worse than the
 * database read it saved. The lease bounds that to this window instead of forever: a conversation quiet longer than it
 * pays one D1 read on open and re-seeds, and an active one refreshes on every message and never touches D1.
 */
export const MESSAGE_CACHE_LEASE_MS = TIME_UNITS.HOUR_MS;

/**
 * The `batch_count` of a chat-history read that wants ONLY the newest group -- what a conversation shows on open.
 *
 * Named rather than spelled `1` at each site because it is the PRODUCT decision this cache exists to make, not a tuning
 * knob, and it has two spellings to keep in step: the socket's own backfill leg and the client's first
 * `fetchRecentMessages`. A reader who sees a bare `1` cannot tell a deliberate one-group open from an unfinished
 * default -- the same argument `SERIAL_BATCH_WINDOW` is named under.
 */
export const NEWEST_GROUP_ONLY = 1;

export const CHAT_STORAGE_KEYS = {
	PARTICIPANTS: 'participants',
	CONVERSATION_ID: 'conversationId',
	CURRENT_MESSAGES_BATCH_INDEX: 'currentMessagesBatchIndex',
	MESSAGES_BATCH_PREFIX: 'messages_batch_',
	/** Epoch-ms stamp of the last hydration or append -- the LEASE, read against {@link MESSAGE_CACHE_LEASE_MS}. NEVER
	 *  branch on `messages.length`: a conversation with genuinely zero messages has the same length as one never
	 *  hydrated, so a length test can neither engage the cache for an empty conversation nor tell absent from empty. */
	MESSAGES_HYDRATED_AT: 'messagesHydratedAt',
} as const;

/** Prefix for legacy Creator-sourced image_key values that must be preserved during sync. */
export const CREATOR_IMAGE_KEY_PREFIX = '<img';

/** User Durable Object payload keys. */
export const USER_DO_KEYS = {
	BANC_ACCOUNT_LIST: 'banc_account_list',
	USER_TYPE: 'user_type',
} as const;

/**
 * The two spellings of the current-balance field on a heterogeneous account or user record, ordered by lookup
 * preference -- the same shape as {@link USER_RECORD_ID_KEYS} and {@link BANC_ACCOUNT_ID_KEYS} below.
 *
 * THE CAPITALISED TWIN IS A READ-ONLY LEGACY ACCEPT, not a spelling anyone may write: `persistBalanceStep` was its
 * only producer and stopped writing it on 2026-08-15, but DO storage is PERSISTENT, so pre-2026-08-15 `USER_DATA`
 * records still carry it until the next D1 sync or RPC adjust corrects them (`resolveBalanceFromData`,
 * `tools/users/balance-operations.ts`, carries the full account).
 *
 * HOMED AS A PAIR because the PRECEDENCE is the decision, and it was hand-spelled at four sites -- one of which
 * (`tools/websocket/ws-balance.ts`) re-spelled BOTH literals rather than composing either SSOT. A pair restated per
 * reader is a pair that can be ordered differently per reader, and the reader that loses is the one whose record
 * carries both.
 */
export const CURRENT_BALANCE_KEYS = [DO_STORAGE_KEYS.CURRENT_BALANCE, 'Current_Balance'] as const;

/**
 * The balance-bearing keys a CLIENT-SUPPLIED `userData` record may never carry into the UserDO user_data merge.
 *
 * DERIVED from {@link CURRENT_BALANCE_KEYS} and {@link USER_DO_KEYS} rather than re-spelled, so the capitalised legacy
 * twin has ONE home rather than one per list. Every member is a key
 * `extractCurrentBalance` (`flows/user-data-persist.ts`) resolves the merged `current_balance` from, and the nested
 * `banc_account_list` entry is its FIRST preference -- ahead of the DO's own figure -- so a frame carrying one defines
 * the number `rpcGetBuyingPower` -> `resolveUserBalance` -> `buyingPowerCoversBid` spends.
 *
 * The ONLY legitimate producer of these keys on a `userData` record is the D1 projection
 * `sendBalanceUpdateToUserDO` (`tools/users/do-operations.ts`), which reaches the same merge through the TRUSTED
 * `/createUser` route rather than the WebSocket frame -- which is why the strip belongs at the WS boundary
 * (`stripFrameBalanceKeys`, `utils/users/strip-frame-balance-keys.ts`) and not inside the shared handler.
 */
export const CLIENT_UNTRUSTED_BALANCE_KEYS = [...CURRENT_BALANCE_KEYS, USER_DO_KEYS.BANC_ACCOUNT_LIST] as const;

/**
 * The two spellings of the buying-power field on an inbound balance FRAME, ordered by READ preference -- the same
 * pair-with-precedence shape as {@link CURRENT_BALANCE_KEYS} above, and homed for the same reason: the precedence IS
 * the decision, and a pair restated per reader is a pair that can be ordered differently per reader.
 *
 * `Buying_Power` is the spelling every PRODUCER writes (`sendWsBalanceUpdate`, `utils/websocket/balance-update.ts`,
 * and the PropertyDO bid/refund/data-retrieval frames). `BuyingPower` is a READ-ONLY legacy accept with no live
 * producer, which is why it is FIRST but never written: a frame carrying it is older than the current writer and its
 * value is the one the client last saw.
 *
 * Consumed by `parseCurrentBalance` (`tools/websocket/ws-balance.ts`), which used to re-spell both literals in a
 * hand-rolled `isPresentValue` chain.
 */
export const BUYING_POWER_KEYS = ['BuyingPower', 'Buying_Power'] as const;

/** Legacy alias keys holding the user id on heterogeneous user records. Ordered by lookup preference; consumers take the first defined key. */
export const USER_RECORD_ID_KEYS = ['id', 'ID'] as const;

/** Legacy alias keys holding the banc account id, ordered by lookup preference. The lowercase `banc_account1` alias is deliberately canonical -- the API normalizer AND the DO-stub path must both honor it. */
export const BANC_ACCOUNT_ID_KEYS = ['banc_account_id', 'Banc_Account1', 'banc_account1'] as const;

// CHAT

/** Newest-first groups of MESSAGE_BATCH_SIZE a chat history read returns when the caller names no batch_count: the newest group alone. */
export const CHAT_HISTORY_BATCH_COUNT_DEFAULT = 1;
